import React from "react";
import { Box, MenuItem } from "@mui/material";
import {
  initPartner,
  PartnerHierarchyItem,
  PartnerType,
} from "../models/PartnersModel";

/** Box-drawing: branch mid / last child (tree lines in dropdown). */
const TREE_MID = "\u251c\u2500\u2500 ";
const TREE_END = "\u2514\u2500\u2500 ";

/** Select `value` for "all partners under this billing company" (CompanyId rollup). */
export const BILLING_COMPANY_SELECT_PREFIX = "__co_";

export function billingCompanySelectValue(companyId: number): string {
  return `${BILLING_COMPANY_SELECT_PREFIX}${companyId}`;
}

/**
 * Resolves the partner id sent to billing APIs. Any partner in a company works as anchor;
 * SQL expands to all rows sharing the same CompanyId.
 */
/** 1 = expand to all partners under same dbo.Companies row (parent / "all locations"); 0 = selected partner only. */
export function billingCompanyRollupForApi(value: number | string): number {
  if (value === 0 || value === "0") return 1;
  if (
    typeof value === "string" &&
    value.startsWith(BILLING_COMPANY_SELECT_PREFIX)
  ) {
    return 1;
  }
  return 0;
}

export function billingPartnerIdForApi(
  value: number | string,
  hierarchy: PartnerHierarchyItem[]
): string {
  if (value === 0 || value === "0") return "0";
  if (
    typeof value === "string" &&
    value.startsWith(BILLING_COMPANY_SELECT_PREFIX)
  ) {
    const companyId = Number(value.slice(BILLING_COMPANY_SELECT_PREFIX.length));
    if (Number.isNaN(companyId)) return "0";
    const rows = hierarchy
      .filter((p) => p.companyId === companyId)
      .sort((a, b) => a.customerName.localeCompare(b.customerName));
    return rows[0] ? String(rows[0].partnersId) : "0";
  }
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n) || n <= 0) return "0";
  return String(n);
}

/** Partner row used for PDF labels / grouping when a company rollup is selected. */
export function billingSelectionFromSelectValue(
  raw: number | string,
  hierarchy: PartnerHierarchyItem[]
): PartnerType {
  if (raw === 0 || raw === "0") {
    return { ...initPartner };
  }
  if (
    typeof raw === "string" &&
    raw.startsWith(BILLING_COMPANY_SELECT_PREFIX)
  ) {
    const companyId = Number(raw.slice(BILLING_COMPANY_SELECT_PREFIX.length));
    const rows = hierarchy
      .filter((p) => p.companyId === companyId)
      .sort((a, b) => a.customerName.localeCompare(b.customerName));
    const first = rows[0];
    if (!first) {
      return { ...initPartner };
    }
    const companyLabel =
      first.companyName?.trim() || `Company ${companyId}`;
    return {
      partnersId: first.partnersId,
      customerName: companyLabel,
      isDiligentSearch: null,
    };
  }
  const n = typeof raw === "number" ? raw : Number(raw);
  if (Number.isNaN(n) || n <= 0) {
    return { ...initPartner };
  }
  const row = hierarchy.find((c) => c.partnersId === n);
  return row
    ? {
        partnersId: row.partnersId,
        customerName: row.customerName,
        isDiligentSearch: null,
      }
    : { ...initPartner };
}

/** Plain label for the closed Select (avoids showing tree glyphs from MenuItem children). */
export function billingSelectDisplayLabel(
  value: number | string,
  hierarchy: PartnerHierarchyItem[]
): string {
  if (value === "" || value === undefined || value === null) return "";
  if (value === 0 || value === "0") return "All";
  const sel = billingSelectionFromSelectValue(value, hierarchy);
  const name = sel.customerName?.trim();
  if (name) return name;
  return typeof value === "number" || (typeof value === "string" && /^\d+$/.test(value))
    ? `Partner ${value}`
    : String(value);
}

/** Flat A–Z client list for billing Autocomplete (by CustomerName). */
export function sortPartnersByCustomerName(
  partners: PartnerHierarchyItem[]
): PartnerHierarchyItem[] {
  return [...partners].sort((a, b) =>
    (a.customerName || "").localeCompare(b.customerName || "", undefined, {
      sensitivity: "base",
    })
  );
}

/**
 * Builds Select menu content: company groups (dbo.Companies) as a tree-style block,
 * then standalone clients (no CompanyId). Company row is selectable (rollup for all children).
 */
export function renderPartnerBillingSelectItems(
  partnersHierarchy: PartnerHierarchyItem[]
): React.ReactNode[] {
  const items: React.ReactNode[] = [];
  const byCompany = new Map<number, { name: string; rows: PartnerHierarchyItem[] }>();

  for (const p of partnersHierarchy) {
    if (p.companyId != null) {
      const cur = byCompany.get(p.companyId) ?? {
        name: p.companyName || `Company ${p.companyId}`,
        rows: [],
      };
      cur.rows.push(p);
      if (p.companyName) cur.name = p.companyName;
      byCompany.set(p.companyId, cur);
    }
  }

  const sortedCompanies = Array.from(byCompany.entries()).sort((a, b) =>
    a[1].name.localeCompare(b[1].name)
  );
  for (const [companyId, grp] of sortedCompanies) {
    items.push(
      <MenuItem
        key={`co-${companyId}`}
        className="custom-menu-item"
        value={billingCompanySelectValue(companyId)}
        sx={(theme) => ({
          py: 1,
          pl: 1.5,
          pr: 2,
          lineHeight: 1.35,
          fontSize: "0.9375rem",
          fontWeight: 700,
          letterSpacing: "0.01em",
          color: "text.primary",
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[800]
              : theme.palette.grey[200],
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          "&:hover": {
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[700]
                : theme.palette.grey[300],
          },
          "&.Mui-selected": {
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[700]
                : theme.palette.grey[300],
            borderLeftColor: theme.palette.primary.dark,
            "&:hover": {
              bgcolor:
                theme.palette.mode === "dark"
                  ? theme.palette.grey[600]
                  : theme.palette.grey[400],
            },
          },
          "&.Mui-selected.Mui-focusVisible": {
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[600]
                : theme.palette.grey[400],
          },
        })}
      >
        {grp.name}
      </MenuItem>
    );
    grp.rows.sort((a, b) => a.customerName.localeCompare(b.customerName));
    const n = grp.rows.length;
    grp.rows.forEach((c, i) => {
      const isLast = i === n - 1;
      items.push(
        <MenuItem
          key={c.partnersId}
          className="custom-menu-item"
          value={c.partnersId}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            pl: 2,
            py: 0.75,
            whiteSpace: "normal",
          }}
        >
          <Box
            component="span"
            sx={{
              display: "flex",
              flexShrink: 0,
              width: "2em",
              minWidth: "2em",
              justifyContent: "flex-end",
              pr: 0.25,
              fontFamily:
                'ui-monospace, "Cascadia Code", "Segoe UI Mono", Consolas, monospace',
              fontSize: "0.8125rem",
              color: "text.secondary",
              lineHeight: 1.5,
              userSelect: "none",
            }}
            aria-hidden
          >
            {isLast ? TREE_END : TREE_MID}
          </Box>
          <Box
            component="span"
            sx={{ flex: 1, minWidth: 0, lineHeight: 1.5, pl: 0.25 }}
          >
            {c.customerName}
          </Box>
        </MenuItem>
      );
    });
  }

  const standalone = partnersHierarchy
    .filter((p) => p.companyId == null)
    .sort((a, b) => a.customerName.localeCompare(b.customerName));
  for (const item of standalone) {
    items.push(
      <MenuItem
        key={item.partnersId}
        className="custom-menu-item"
        value={item.partnersId}
      >
        {item.customerName}
      </MenuItem>
    );
  }

  return items;
}

/** Option row for MUI Autocomplete (same values as the billing Select). */
export interface PartnerBillingAutocompleteOption {
  selectValue: number | string;
  /** Label when selected (no tree glyphs). */
  label: string;
  isCompanyRow?: boolean;
  /** Tree branch glyph for child rows in the dropdown only. */
  treePrefix?: string;
}

function normalizeBillingSelectValue(v: number | string): string {
  if (v === 0 || v === "0") return "0";
  return String(v);
}

export function findPartnerBillingAutocompleteOption(
  options: PartnerBillingAutocompleteOption[],
  billingSelectValue: number | string
): PartnerBillingAutocompleteOption | null {
  const want = normalizeBillingSelectValue(billingSelectValue);
  return (
    options.find(
      (o) => normalizeBillingSelectValue(o.selectValue) === want
    ) ?? null
  );
}

/**
 * Flat options for Autocomplete: All, company rollups, partners under companies (with tree prefixes), standalone partners.
 */
export function buildPartnerBillingAutocompleteOptions(
  partnersHierarchy: PartnerHierarchyItem[]
): PartnerBillingAutocompleteOption[] {
  const out: PartnerBillingAutocompleteOption[] = [
    { selectValue: "0", label: "All" },
  ];

  const byCompany = new Map<
    number,
    { name: string; rows: PartnerHierarchyItem[] }
  >();

  for (const p of partnersHierarchy) {
    if (p.companyId != null) {
      const cur = byCompany.get(p.companyId) ?? {
        name: p.companyName || `Company ${p.companyId}`,
        rows: [],
      };
      cur.rows.push(p);
      if (p.companyName) cur.name = p.companyName;
      byCompany.set(p.companyId, cur);
    }
  }

  const sortedCompanies = Array.from(byCompany.entries()).sort((a, b) =>
    a[1].name.localeCompare(b[1].name)
  );

  for (const [companyId, grp] of sortedCompanies) {
    out.push({
      selectValue: billingCompanySelectValue(companyId),
      label: grp.name,
      isCompanyRow: true,
    });
    grp.rows.sort((a, b) => a.customerName.localeCompare(b.customerName));
    const n = grp.rows.length;
    grp.rows.forEach((c, i) => {
      const isLast = i === n - 1;
      out.push({
        selectValue: c.partnersId,
        label: c.customerName,
        treePrefix: isLast ? TREE_END : TREE_MID,
      });
    });
  }

  const standalone = partnersHierarchy
    .filter((p) => p.companyId == null)
    .sort((a, b) => a.customerName.localeCompare(b.customerName));
  for (const item of standalone) {
    out.push({
      selectValue: item.partnersId,
      label: item.customerName,
    });
  }

  return out;
}
